import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useScrollLock } from "../../hooks/useScrollLock";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { gsap } from "gsap";
import {
  Search,
  ShoppingCart,
  User,
  Heart,
  Grid,
  Package,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  LogOut,
  Settings,
  MessageCircle,
  Wallet,
  Layers,
  Flame,
  Trash2,
  Clock,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { useCart } from "../../contexts/CartContext";
import { useWishlist } from "../../contexts/WishlistContext";
import { useTopLevelCategories } from "../../hooks/useCategories";
import { formatCurrencyNPR } from "../../utils/currency";
import { getProductPrice } from "../../utils/productUtils";
import ProfilePicture from "../ProfilePicture";
import { productService } from "../../services/firebaseConfig";
import { sanitizeString } from "../../utils/sanitization";
import {
  sanitizeSearchQuery,
  isValidSearchQuery,
} from "../../utils/searchExpand";
import { secureLog } from "../../lib/errors/logger";
import { Category } from "../../types";
import { useSubcategories } from "../../hooks/useCategories";
import {
  saveRecentSearch,
  getRecentSearches,
  clearRecentSearches,
  removeRecentSearch,
} from "../../utils/searchUtils";
// Logo icon - using public favicon
const logoIcon = "/favicon.ico";

// Search Discovery Suggestions - Popular/Trending searches
const SEARCH_DISCOVERY_SUGGESTIONS = [
  { text: "New Arrivals", isHot: true },
  { text: "Saree", isHot: true },
  { text: "Kurta Set", isHot: true },
  { text: "Shoes", isHot: false },
  { text: "Bags", isHot: false },
  { text: "Tops", isHot: true },
  { text: "Jeans", isHot: false },
  { text: "Watch", isHot: false },
  { text: "Dress", isHot: false },
  { text: "Sunglasses", isHot: false },
  { text: "Jacket", isHot: false },
  { text: "Earrings", isHot: true },
];

const Header: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<
    Array<{
      id: string;
      title: string;
      image: string;
      price: number;
      product: any;
      brand?: string;
    }>
  >([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isSearchHiding, setIsSearchHiding] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [showCategoriesMenu, setShowCategoriesMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hoveredCategoryId, setHoveredCategoryId] = useState<string | null>(
    null
  );
  // Cache for prefetched subcategories to prevent loading flicker
  const [subcategoriesCache, setSubcategoriesCache] = useState<
    Record<string, Category[]>
  >({});
  const hasPrefetchedRef = useRef(false);

  const searchRef = useRef<HTMLDivElement>(null);
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userMenuDropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuDropdownRef = useRef<HTMLDivElement>(null);
  const categoriesMenuRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSearchHidingRef = useRef(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const menuOpenTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isHoveringMenuRef = useRef(false);
  const mobileMenuButtonClickedRef = useRef(false);
  const searchButtonClickedRef = useRef(false);
  const messageButtonClickedRef = useRef(false);

  const queryClient = useQueryClient();

  const { currentUser, signOut } = useAuth();
  const { items: cartItems } = useCart();
  const { wishlistItems } = useWishlist();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    data: categories = [] as Category[],
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useTopLevelCategories();
  useEffect(() => {
    if (categoriesError) {
      // Log error securely for debugging
      secureLog("error", "Failed to fetch categories in Header", {
        error:
          categoriesError instanceof Error
            ? categoriesError.message
            : "Unknown error",
      });
    }
  }, [categoriesError]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Load recent searches on mount and when search opens
  useEffect(() => {
    if (isSearchOpen) {
      setRecentSearches(getRecentSearches());
    }
  }, [isSearchOpen]);

  // Keep a ref for the hiding state to avoid duplicate timers
  useEffect(() => {
    isSearchHidingRef.current = isSearchHiding;
  }, [isSearchHiding]);

  // Animate search dropdown
  useEffect(() => {
    if (isSearchOpen && searchDropdownRef.current && !isSearchHiding) {
      gsap.fromTo(
        searchDropdownRef.current,
        { opacity: 0, y: -10 },
        { opacity: 1, y: 0, duration: 0.12, ease: "power2.out" }
      );
    } else if (isSearchHiding && searchDropdownRef.current) {
      gsap.to(searchDropdownRef.current, {
        opacity: 0,
        y: -10,
        duration: 0.12,
        ease: "power2.in"
      });
    }
  }, [isSearchOpen, isSearchHiding]);

  // Animate search buttons with stagger
  useEffect(() => {
    if (isSearchOpen && searchDropdownRef.current) {
      // Use setTimeout to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        if (searchDropdownRef.current) {
          const buttons = searchDropdownRef.current.querySelectorAll('button');
          if (buttons.length > 0) {
            gsap.fromTo(
              buttons,
              { opacity: 0, scale: 0.9 },
              {
                opacity: 1,
                scale: 1,
                duration: 0.2,
                stagger: 0.02,
                ease: "back.out(1.7)"
              }
            );
          }
        }
      }, 10);
      return () => clearTimeout(timeoutId);
    }
  }, [isSearchOpen, searchQuery, recentSearches]);

  // Animate search results with stagger
  useEffect(() => {
    if (isSearchOpen && searchQuery.length > 0 && searchResults.length > 0 && searchDropdownRef.current) {
      // Use setTimeout to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        if (searchDropdownRef.current) {
          const results = searchDropdownRef.current.querySelectorAll('[data-search-result]');
          if (results.length > 0) {
            gsap.fromTo(
              results,
              { opacity: 0, x: -10 },
              {
                opacity: 1,
                x: 0,
                duration: 0.2,
                stagger: 0.03,
                ease: "power2.out"
              }
            );
          }
        }
      }, 10);
      return () => clearTimeout(timeoutId);
    }
  }, [isSearchOpen, searchResults]);

  // Animate user menu dropdown
  useEffect(() => {
    if (showUserMenu && userMenuDropdownRef.current) {
      gsap.fromTo(
        userMenuDropdownRef.current,
        { opacity: 0, y: -10 },
        { opacity: 1, y: 0, duration: 0.15, ease: "power2.out" }
      );
    } else if (!showUserMenu && userMenuDropdownRef.current) {
      gsap.to(userMenuDropdownRef.current, {
        opacity: 0,
        y: -10,
        duration: 0.15,
        ease: "power2.in",
        onComplete: () => {
          if (userMenuDropdownRef.current) {
            userMenuDropdownRef.current.style.display = 'none';
          }
        }
      });
    }
  }, [showUserMenu]);

  // Animate mobile menu dropdown
  useEffect(() => {
    if (isMenuOpen && mobileMenuDropdownRef.current) {
      gsap.fromTo(
        mobileMenuDropdownRef.current,
        { opacity: 0, y: -10 },
        { opacity: 1, y: 0, duration: 0.16, ease: "power2.out" }
      );
    } else if (!isMenuOpen && mobileMenuDropdownRef.current) {
      gsap.to(mobileMenuDropdownRef.current, {
        opacity: 0,
        y: -10,
        duration: 0.16,
        ease: "power2.in",
        onComplete: () => {
          if (mobileMenuDropdownRef.current) {
            mobileMenuDropdownRef.current.style.display = 'none';
          }
        }
      });
    }
  }, [isMenuOpen]);

  const closeSearchDelayed = useCallback((opts?: { clearQuery?: boolean; delay?: number }) => {
    const { clearQuery = true, delay = 160 } = opts || {};
    if (isSearchHidingRef.current) return;
    setIsSearchHiding(true);
    // collapse expanded UI immediately but keep dropdown mounted for exit animation
    setIsSearchExpanded(false);
    setTimeout(() => {
      setIsSearchOpen(false);
      setIsSearchHiding(false);
      if (clearQuery) {
        setSearchQuery("");
        setSearchResults([]);
      }
    }, delay);
  }, []);

  // Prefetch all subcategories ONCE when categories are loaded
  // This runs only once per session, not on every hover
  const prefetchAllSubcategories = useCallback(async () => {
    // Only prefetch once per session
    if (hasPrefetchedRef.current || categories.length === 0) return;
    hasPrefetchedRef.current = true;

    try {
      const { categoryService } = await import("../../services/firebaseConfig");
      const cache: Record<string, Category[]> = {};

      // Prefetch all subcategories in parallel (one-time fetch)
      const results = await Promise.all(
        categories.map(async (category: Category) => {
          // Check if already in React Query cache
          const cached = queryClient.getQueryData<Category[]>([
            "categories",
            "subcategories",
            category.id,
          ]);
          if (cached) {
            return { id: category.id, subs: cached };
          }

          try {
            const subs = await categoryService.getSubcategories(category.id);
            // Populate React Query cache with long stale time
            queryClient.setQueryData(
              ["categories", "subcategories", category.id],
              subs,
              {
                updatedAt: Date.now(),
              }
            );
            return { id: category.id, subs };
          } catch {
            return { id: category.id, subs: [] };
          }
        })
      );

      // Build cache object
      results.forEach(({ id, subs }) => {
        cache[id] = subs;
      });

      setSubcategoriesCache(cache);
    } catch (error) {
      // Silent fail - will fall back to individual fetching
      hasPrefetchedRef.current = false; // Allow retry on error
    }
  }, [categories, queryClient]);

  // Immediate hover handler for categories (no delay to prevent flickering)
  const handleCategoryHover = useCallback((categoryId: string) => {
    // Clear any pending leave timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    // Set immediately for smooth experience
    setHoveredCategoryId(categoryId);
  }, []);

  const handleCategoryLeave = useCallback(() => {
    // Clear existing timeout first
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    // Small delay to allow mouse movement to subcategory panel
    hoverTimeoutRef.current = setTimeout(() => {
      if (!isHoveringMenuRef.current) {
        setHoveredCategoryId(null);
      }
    }, 150);
  }, []);

  // Handle menu open with prefetching
  const handleMenuOpen = useCallback(() => {
    isHoveringMenuRef.current = true;
    // Clear any pending close timeout
    if (menuOpenTimeoutRef.current) {
      clearTimeout(menuOpenTimeoutRef.current);
    }
    setShowCategoriesMenu(true);
    // Auto-select first category after a brief moment
    if (categories.length > 0 && !hoveredCategoryId) {
      setTimeout(() => {
        setHoveredCategoryId(categories[0].id);
      }, 50);
    }
    // Prefetch if not already done (backup)
    if (!hasPrefetchedRef.current) {
      prefetchAllSubcategories();
    }
  }, [categories, hoveredCategoryId, prefetchAllSubcategories]);

  const handleMenuClose = useCallback(() => {
    isHoveringMenuRef.current = false;
    // Clear any pending hover timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    // Delay closing to allow movement back to menu
    menuOpenTimeoutRef.current = setTimeout(() => {
      if (!isHoveringMenuRef.current) {
        setShowCategoriesMenu(false);
        setHoveredCategoryId(null);
      }
    }, 100);
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (menuOpenTimeoutRef.current) clearTimeout(menuOpenTimeoutRef.current);
    };
  }, []);


  // Prefetch subcategories when categories are loaded (runs once)
  useEffect(() => {
    if (categories.length > 0 && !hasPrefetchedRef.current) {
      // Small delay to not block initial render
      const timer = setTimeout(() => {
        prefetchAllSubcategories();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [categories, prefetchAllSubcategories]);

  // Keyboard navigation for categories dropdown
  useEffect(() => {
    if (!showCategoriesMenu) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowCategoriesMenu(false);
        setHoveredCategoryId(null);
      }
      // Tab navigation is handled natively by browser
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showCategoriesMenu]);

  // Ensure Firebase is ready on component mount
  useEffect(() => {
    // Small delay to ensure Firebase services are initialized
    const checkFirebaseReady = () => {
      if ((window as any).__FIREBASE_CONFIG_ERROR__) {
        // Firebase config error - search won't work
        return;
      }

      // Verify productService is available
      if (
        !productService ||
        typeof productService.superSearchProducts !== "function"
      ) {
        // Service not ready yet - will retry on first search
        return;
      }
    };

    // Check after a short delay to allow Firebase to initialize
    const timer = setTimeout(checkFirebaseReady, 100);
    return () => clearTimeout(timer);
  }, []);

  const cartItemCount = cartItems.length;
  const wishlistItemCount = wishlistItems.length;

  // Lock scroll when search is expanded on mobile
  useScrollLock(isSearchExpanded);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      
      // Don't close if buttons were just clicked
      if (mobileMenuButtonClickedRef.current || 
          searchButtonClickedRef.current || 
          messageButtonClickedRef.current) {
        return;
      }
      
      // Check if click is on the mobile menu button itself
      const mobileMenuButton = mobileMenuRef.current?.querySelector('button');
      if (mobileMenuButton && (mobileMenuButton === target || mobileMenuButton.contains(target))) {
        return;
      }
      
      // Check if click is on search button
      const searchButton = document.querySelector('[aria-label="Open search"]');
      if (searchButton && (searchButton === target || searchButton.contains(target))) {
        return;
      }
      
      // Check if click is on message button
      const messageButton = document.querySelector('[aria-label="Messages"]');
      if (messageButton && (messageButton === target || messageButton.contains(target))) {
        return;
      }
      
      if (
        searchRef.current &&
        !searchRef.current.contains(target)
      ) {
        closeSearchDelayed();
      }
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(target)
      ) {
        setShowUserMenu(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(target)
      ) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const handleSearch = async (query: string) => {
    setSearchQuery(query);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    // Debounce search to avoid too many API calls
    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      // SECURITY: Use comprehensive search query sanitization
      // This prevents NoSQL injection, XSS, command injection, and other attacks
      const sanitizedQuery = sanitizeSearchQuery(query, 100);

      if (
        !sanitizedQuery ||
        sanitizedQuery.length < 2 ||
        !isValidSearchQuery(sanitizedQuery)
      ) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      // Check if Firebase is ready before searching
      const isFirebaseReady = () => {
        // Check for Firebase config errors
        if ((window as any).__FIREBASE_CONFIG_ERROR__) {
          return false;
        }

        // Check if productService and superSearchProducts are available
        if (
          !productService ||
          typeof productService.superSearchProducts !== "function"
        ) {
          return false;
        }

        return true;
      };

      // Retry logic: Try up to 3 times with increasing delays if Firebase isn't ready
      const performSearch = async (retryCount = 0): Promise<void> => {
        const maxRetries = 3;
        const retryDelay =     200 * (retryCount + 1); // 200ms, 400ms, 600ms

        if (!isFirebaseReady() && retryCount < maxRetries) {
          // Wait a bit for Firebase to initialize, then retry
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          return performSearch(retryCount + 1);
        }

        if (!isFirebaseReady()) {
          // Firebase still not ready after retries - return empty results
          setSearchResults([]);
          setIsSearching(false);
          return;
        }

        try {
          // Use superSearchProducts for intelligent search with synonyms and better matching
          // This uses the improved search logic with fashion-related synonyms
          const products = await productService.superSearchProducts(
            sanitizedQuery
          );

          // Map products to suggestion format (no store grouping)
          const productResults = products.slice(0, 8).map((product) => ({
            id: product.id,
            title: product.title,
            image: product.images?.[0] || "",
            price: getProductPrice(product),
            product: product,
            brand: product.brand,
          }));

          setSearchResults(productResults);

          // Save to recent searches
          if (sanitizedQuery && sanitizedQuery.length >= 2) {
            saveRecentSearch(sanitizedQuery);
          }
        } catch (error) {
          // Log error without exposing sensitive details
          if (error instanceof Error) {
            // Only log error type, not message or stack
            secureLog("error", "Header search error", {
              errorType: error.name,
            });
          }
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      };

      // Start the search with retry logic
      performSearch();
    }, 300); // 300ms debounce delay
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setShowUserMenu(false);
      navigate("/");
    } catch (error) {
      // SECURITY: Removed console.error to prevent information leakage
    }
  };

  const handleSearchExpand = () => {
    setIsSearchExpanded(true);
    setIsSearchOpen(true);
  };
  const handleSearchCollapse = () => {
    // Use delayed close to allow exit animation to run
    closeSearchDelayed({ clearQuery: true, delay: 160 });
  };

  // Unified navigation icon class for consistent top/bottom nav system (reserved for future use)
  // const navIcon =
  //   "flex items-center justify-center w-10 h-10 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors";

  return (
    <header className="bg-white fixed inset-x-0 top-0 z-50 h-14 md:h-16 border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between gap-4">
        {/* Logo */}
        <Link
          to="/"
          className={`flex items-center gap-2 shrink-0 transition-all duration-150 ${
            isSearchExpanded || isSearchOpen || isSearchHiding
              ? "sm:opacity-100 sm:pointer-events-auto"
              : "opacity-100 pointer-events-auto"
          }`}
        >
          <img
            src={logoIcon}
            alt="Baakas"
            className={`${isSearchExpanded || isSearchOpen || isSearchHiding ? "w-6 h-6 sm:w-8 sm:h-8 md:w-9 md:h-9" : "w-8 h-8 md:w-9 md:h-9"} transition-all duration-150`}
            loading="lazy"
            decoding="async"
            width={36}
            height={36}
          />
          <span className={`${isSearchExpanded || isSearchOpen || isSearchHiding ? "text-sm sm:text-lg md:text-xl" : "text-lg md:text-xl"} font-bold text-gray-900 font-poppins transition-all duration-150`}>
            Baakas
          </span>
        </Link>

        {/* Search */}
        <div
          className={`relative flex-1 max-w-2xl transition-opacity duration-150 ${
            isSearchExpanded || isSearchOpen || isSearchHiding
              ? "opacity-100 pointer-events-auto flex"
              : "opacity-0 pointer-events-none sm:opacity-100 sm:pointer-events-auto sm:flex"
          }`}
          ref={searchRef}
        >
          <div className={`relative w-full ${isSearchExpanded ? "ml-2 sm:ml-0" : ""}`}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="search"
              placeholder="Search products, brands, categories..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => setIsSearchOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && searchQuery.length >= 2) {
                  saveRecentSearch(searchQuery);
                  navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
                  closeSearchDelayed({ clearQuery: true });
                }
              }}
              className="w-full pl-10 pr-10 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 font-poppins"
            />
            {/* Clear button */}
            {searchQuery.length > 0 && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setSearchResults([]);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Mobile search close button */}
          {isSearchExpanded && (
            <button
              onClick={handleSearchCollapse}
              className="sm:hidden ml-2 p-2 text-gray-400 hover:text-gray-600"
              aria-label="Close search"
            >
              <X className="w-5 h-5" />
            </button>
          )}

          {/* Search Results & Recent Searches */}
          {isSearchOpen && (
            <div
              ref={searchDropdownRef}
              className={`${isSearchExpanded ? 'fixed sm:absolute' : 'absolute'} ${isSearchExpanded ? 'left-0 right-0 sm:left-0 sm:right-0' : 'left-0 right-0'} ${isSearchExpanded ? 'mt-0 sm:mt-1 rounded-xl mx-3 sm:mx-0' : 'mt-1 rounded-xl'} bg-white border border-gray-200 shadow-lg z-40 overflow-hidden`}
              style={{ 
                willChange: "transform, opacity",
                ...(isSearchExpanded ? {
                  left: '12px', // 12px margin from left (mx-3 = 12px)
                  right: '12px', // 12px margin from right
                  width: 'calc(100vw - 24px)', // Full width minus margins
                  top: '56px', // h-14 = 56px (mobile header height)
                } : {
                  // Desktop: ensure proper positioning below search input (not overlapping)
                  top: 'calc(100% + 4px)',
                })
              }}
            >
                {/* When query is empty - show Recent Searches + Categories + Discovery */}
                {searchQuery.length === 0 && !isSearching && (
                  <div className="max-h-[70vh] overflow-y-auto">
                    {/* Recently Searched Section - Compact */}
                    {recentSearches.length > 0 && (
                      <div className="px-3 sm:px-4 py-2.5 border-b border-gray-100">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xs font-semibold text-gray-900 font-poppins flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-gray-500" />
                            Recently Searched
                          </h3>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              clearRecentSearches();
                              setRecentSearches([]);
                            }}
                            className="p-1 text-gray-400 hover:text-primary-500 hover:bg-gray-100 rounded transition-colors"
                            aria-label="Clear all recent searches"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {recentSearches.slice(0, 6).map((search, index) => (
                            <button
                              key={index}
                              onClick={() => {
                                setSearchQuery(search);
                                handleSearch(search);
                              }}
                              className="group flex items-center gap-1 px-2.5 py-1.5 bg-gray-50 active:bg-gray-100 text-gray-700 active:text-gray-900 text-xs rounded-md transition-colors font-poppins touch-manipulation"
                            >
                              <span className="truncate max-w-[120px]">{search}</span>
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeRecentSearch(search);
                                  setRecentSearches(getRecentSearches());
                                }}
                                className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-gray-200 rounded transition-all cursor-pointer"
                              >
                                <X className="w-2.5 h-2.5 text-gray-400" />
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Search Discovery Section */}
                    <div className="px-3 sm:px-4 py-3 sm:py-4">
                      <h3 className="text-xs font-semibold text-gray-900 font-poppins flex items-center gap-1.5 mb-3">
                        <Flame className="w-3.5 h-3.5 text-primary-500" />
                        Search Discovery
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {SEARCH_DISCOVERY_SUGGESTIONS.map((suggestion, index) => (
                          <button
                            key={suggestion.text}
                            onClick={() => {
                              setSearchQuery(suggestion.text);
                              handleSearch(suggestion.text);
                            }}
                            className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 bg-gray-50 active:bg-primary-50 text-gray-700 active:text-primary-600 text-xs rounded-full transition-colors font-poppins border border-gray-200 active:border-primary-200 touch-manipulation"
                          >
                            {suggestion.isHot && (
                              <Flame className="w-3 h-3 text-primary-500" />
                            )}
                            <span>{suggestion.text}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Search Results */}
                {searchQuery.length > 0 && (
                  <>
                    {isSearching ? (
                      <div className="divide-y divide-gray-100">
                        {[...Array(5)].map((_, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-3 px-4 py-3 animate-pulse"
                          >
                            <div className="w-12 h-12 bg-gray-200 rounded-lg flex-shrink-0" />
                            <div className="flex-1 space-y-2">
                              <div className="h-4 bg-gray-200 rounded w-3/4" />
                              <div className="h-3 bg-gray-200 rounded w-1/3" />
                            </div>
                            <div className="w-16 h-4 bg-gray-200 rounded" />
                          </div>
                        ))}
                      </div>
                    ) : searchResults.length > 0 ? (
                      <div className="max-h-[400px] overflow-y-auto">
                        {/* Products Header */}
                        <div className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-gray-50 border-b border-gray-100">
                          <Package className="w-4 h-4 text-gray-400" />
                          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide font-poppins">
                            Products
                          </h4>
                        </div>

                        {/* Products List - Row Layout */}
                        <div className="divide-y divide-gray-100">
                          {searchResults.map((result, index) => (
                            <div
                              key={result.id}
                              onClick={() => {
                                saveRecentSearch(searchQuery);
                                navigate(`/product/${result.id}`);
                                closeSearchDelayed({ clearQuery: true });
                              }}
                              className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 active:bg-gray-50 cursor-pointer group transition-colors touch-manipulation"
                              data-search-result
                            >
                              {/* Thumbnail */}
                              <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                                {result.image ? (
                                  <img
                                    src={result.image}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Package className="w-5 h-5 text-gray-400" />
                                  </div>
                                )}
                              </div>

                              {/* Title & Brand */}
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm text-gray-900 truncate font-poppins group-hover:text-primary-600 transition-colors">
                                  {result.title
                                    ? String(result.title).replace(/[<>]/g, "")
                                    : "Product"}
                                </div>
                                {result.brand && (
                                  <div className="text-xs text-gray-600 truncate mt-0.5">
                                    {result.brand}
                                  </div>
                                )}
                              </div>

                              {/* Price */}
                              <div className="flex-shrink-0 text-right">
                                <div className="font-bold text-primary-600 text-sm font-poppins">
                                  {formatCurrencyNPR(result.price)}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* View All Results */}
                        {searchQuery.length >= 2 && (
                          <div
                            onClick={() => {
                              saveRecentSearch(searchQuery);
                              navigate(
                                `/search?q=${encodeURIComponent(searchQuery)}`
                              );
                              closeSearchDelayed({ clearQuery: true });
                            }}
                            className="p-3 text-center text-primary-600 hover:bg-primary-50 cursor-pointer border-t border-gray-200 font-semibold text-sm transition-all duration-200 font-poppins"
                          >
                            View all results for "{searchQuery}"
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="p-8 text-center">
                        <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-600 font-medium mb-1 font-poppins">
                          No results found
                        </p>
                        <p className="text-sm text-gray-500">
                          Try different keywords or browse categories
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-3 shrink-0">
          {/* Mobile search button - Always visible and interactive */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              searchButtonClickedRef.current = true;
              handleSearchExpand();
              setTimeout(() => {
                searchButtonClickedRef.current = false;
              }, 100);
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
              searchButtonClickedRef.current = true;
            }}
            className="sm:hidden p-2 text-gray-600 active:text-gray-900 transition-colors touch-manipulation"
            aria-label="Open search"
            type="button"
          >
            <Search className="w-5 h-5" />
          </button>

          {/* Desktop-only and conditional elements */}
          <div
            className={`flex items-center gap-3 transition-opacity duration-150 ${
              isSearchExpanded || isSearchOpen || isSearchHiding
                ? "opacity-0 pointer-events-none sm:opacity-100 sm:pointer-events-auto"
                : "opacity-100 pointer-events-auto"
            }`}
          >
          {/* Categories (desktop) - Premium Mega Menu */}
          <div
            className="relative hidden lg:block"
            ref={categoriesMenuRef}
            onMouseEnter={handleMenuOpen}
            onMouseLeave={handleMenuClose}
          >
            <Link
              to="/categories"
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-all duration-200 rounded-lg ${
                showCategoriesMenu
                  ? "text-primary-600 bg-primary-50"
                  : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
              }`}
              aria-label="Browse categories"
              aria-expanded={showCategoriesMenu}
            >
              <Grid className="w-4 h-4" />
              <span>Categories</span>
              <ChevronDown
                className={`w-3 h-3 transition-transform duration-200 ${
                  showCategoriesMenu ? "rotate-180" : ""
                }`}
              />
            </Link>

            {/* Invisible bridge to prevent gap flicker */}
            {showCategoriesMenu && (
              <div className="absolute top-full left-0 right-0 h-2 bg-transparent" />
            )}

            {showCategoriesMenu && (
              <div
                className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-40 overflow-hidden"
                style={{ width: "520px", maxWidth: "90vw" }}
                role="menu"
                aria-label="Category navigation menu"
                onMouseEnter={() => {
                  isHoveringMenuRef.current = true;
                  if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current);
                  }
                  if (menuOpenTimeoutRef.current) {
                    clearTimeout(menuOpenTimeoutRef.current);
                  }
                }}
                onMouseLeave={handleMenuClose}
              >
                <div className="flex min-h-[320px]">
                  {/* Column 1: Main Categories with Icons */}
                  <div className="w-[220px] bg-gradient-to-b from-gray-50 to-white border-r border-gray-100 flex-shrink-0">
                    {categoriesLoading ? (
                      <div className="p-3 space-y-1">
                        {[...Array(6)].map((_, i) => (
                          <div
                            key={i}
                            className="h-12 bg-gray-100 rounded-lg animate-pulse"
                          />
                        ))}
                      </div>
                    ) : categoriesError ? (
                      <div className="p-4 text-sm text-red-500 text-center">
                        Failed to load categories
                      </div>
                    ) : categories.length > 0 ? (
                      <nav className="py-2" role="navigation">
                        {categories.map((category: Category) => {
                          const isActive = hoveredCategoryId === category.id;
                          const categoryImage =
                            category.imageUrl ||
                            category.thumbnailUrl ||
                            category.image;

                          return (
                            <div
                              key={category.id}
                              onMouseEnter={() =>
                                handleCategoryHover(category.id)
                              }
                              className={`group mx-2 mb-0.5 rounded-lg transition-all duration-150 cursor-pointer ${
                                isActive
                                  ? "bg-primary-500 shadow-md shadow-primary-500/20"
                                  : "hover:bg-gray-100"
                              }`}
                            >
                              <Link
                                to="/categories"
                                className="flex items-center gap-3 px-3 py-2.5"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowCategoriesMenu(false);
                                  setHoveredCategoryId(null);
                                }}
                              >
                                {/* Category Icon/Image */}
                                <div
                                  className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden ${
                                    isActive
                                      ? "bg-white/20"
                                      : "bg-gray-100 group-hover:bg-white"
                                  }`}
                                >
                                  {categoryImage ? (
                                    <img
                                      src={categoryImage}
                                      alt=""
                                      className="w-full h-full object-cover"
                                      loading="lazy"
                                      onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                        e.currentTarget.nextElementSibling?.classList.remove(
                                          "hidden"
                                        );
                                      }}
                                    />
                                  ) : null}
                                  <Layers
                                    className={`w-4 h-4 ${
                                      categoryImage ? "hidden" : ""
                                    } ${
                                      isActive ? "text-white" : "text-gray-500"
                                    }`}
                                  />
                                </div>

                                {/* Category Name */}
                                <span
                                  className={`flex-1 text-sm font-medium truncate ${
                                    isActive ? "text-white" : "text-gray-700"
                                  }`}
                                >
                                  {category.name}
                                </span>

                                {/* Arrow indicator */}
                                <ChevronRight
                                  className={`w-4 h-4 transition-transform duration-150 ${
                                    isActive
                                      ? "text-white/80 translate-x-0.5"
                                      : "text-gray-400 group-hover:text-gray-600"
                                  }`}
                                />
                              </Link>
                            </div>
                          );
                        })}
                      </nav>
                    ) : (
                      <div className="p-4 text-sm text-gray-500 text-center">
                        No categories available
                      </div>
                    )}
                  </div>

                  {/* Column 2: Subcategories - Shows instantly from cache */}
                  <div className="flex-1 bg-white overflow-hidden">
                    {hoveredCategoryId ? (
                      <SubcategoryColumn
                        key={hoveredCategoryId}
                        categoryId={hoveredCategoryId}
                        categoryName={
                          categories.find(
                            (c: Category) => c.id === hoveredCategoryId
                          )?.name || "Category"
                        }
                        categoryImage={
                          categories.find(
                            (c: Category) => c.id === hoveredCategoryId
                          )?.imageUrl ||
                          categories.find(
                            (c: Category) => c.id === hoveredCategoryId
                          )?.thumbnailUrl
                        }
                        cachedSubcategories={
                          subcategoriesCache[hoveredCategoryId]
                        }
                        onClose={() => {
                          setShowCategoriesMenu(false);
                          setHoveredCategoryId(null);
                        }}
                        hoverTimeoutRef={hoverTimeoutRef}
                        isHoveringMenuRef={isHoveringMenuRef}
                      />
                    ) : (
                      <div className="h-full flex items-center justify-center p-8">
                        <div className="text-center">
                          <Grid className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                          <p className="text-sm text-gray-400">
                            Hover over a category
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Stores (desktop) */}
          <Link
            to="/stores"
            className="hidden lg:flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 hover:text-gray-900 transition-colors"
          >
            <Package className="w-4 h-4" />
            <span>Stores</span>
          </Link>

          {/* Action buttons - grouped */}
          <div className="flex items-center gap-2">
            {/* Cart */}
            <Link
              to="/cart"
              className="relative hidden md:flex items-center justify-center w-9 h-9 text-gray-600 hover:text-gray-900 transition-colors"
              aria-label="Cart"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-4 px-1.5 rounded-full bg-primary-500 text-white text-[10px] font-semibold flex items-center justify-center">
                  {cartItemCount > 99 ? "99+" : cartItemCount}
                </span>
              )}
            </Link>

            {/* Wishlist */}
            <Link
              to="/wishlist"
              className="relative hidden md:flex items-center justify-center w-9 h-9 text-gray-600 hover:text-gray-900 transition-colors"
              aria-label="Wishlist"
            >
              <Heart className="w-5 h-5" />
              {wishlistItemCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-4 px-1.5 rounded-full bg-primary-500 text-white text-[10px] font-semibold flex items-center justify-center">
                  {wishlistItemCount > 99 ? "99+" : wishlistItemCount}
                </span>
              )}
            </Link>
          </div>
          </div>

          {/* Messages - Always visible and interactive */}
          {currentUser && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                messageButtonClickedRef.current = true;
                // Open chat widget by setting URL param
                const currentParams = new URLSearchParams(window.location.search);
                currentParams.set('messaging', 'true');
                window.history.pushState({}, '', `${window.location.pathname}?${currentParams.toString()}`);
                // Trigger a custom event for ChatWidget to listen to
                window.dispatchEvent(new CustomEvent('openChatWidget'));
                setTimeout(() => {
                  messageButtonClickedRef.current = false;
                }, 100);
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                messageButtonClickedRef.current = true;
              }}
              className="relative flex items-center justify-center w-9 h-9 text-gray-600 active:text-gray-900 transition-colors touch-manipulation"
              aria-label="Messages"
            >
              <MessageCircle className="w-5 h-5" />
            </button>
          )}

          {/* User menu */}
          {currentUser ? (
            <div className="relative hidden md:block" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-1.5 w-9 h-9 text-gray-600 hover:text-gray-900 transition-colors"
                aria-label="User menu"
              >
                <ProfilePicture user={currentUser} size="sm" />
                <ChevronDown className="w-3 h-3 text-gray-400" />
              </button>

              {showUserMenu && (
                <div
                  ref={userMenuDropdownRef}
                  className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-40 overflow-hidden"
                  style={{ willChange: "transform, opacity" }}
                >
                    <div className="p-3 border-b border-gray-200">
                      <div className="font-medium text-gray-900 truncate text-sm">
                        {currentUser.displayName || currentUser.email}
                      </div>
                      <div className="text-xs text-gray-600 truncate mt-0.5">
                        {currentUser.email}
                      </div>
                    </div>
                    <div className="py-1">
                      <Link
                        to="/profile"
                        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <User className="w-4 h-4" />
                        <span>Profile</span>
                      </Link>
                      <Link
                        to="/settings"
                        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Settings className="w-4 h-4" />
                        <span>Settings</span>
                      </Link>
                      <div className="border-t border-gray-200 my-1" />
                      <button
                        onClick={() => {
                          handleSignOut();
                          setShowUserMenu(false);
                        }}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors w-full"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Link
                to="/login"
                className="px-3 py-1.5 text-sm text-gray-700 hover:text-gray-900 transition-colors"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="px-3 py-1.5 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded transition-colors"
              >
                Sign Up
              </Link>
            </div>
          )}

          {/* Mobile menu button with dropdown */}
          <div className="md:hidden relative" ref={mobileMenuRef}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                mobileMenuButtonClickedRef.current = true;
                setIsMenuOpen(!isMenuOpen);
                // Reset after state update
                setTimeout(() => {
                  mobileMenuButtonClickedRef.current = false;
                }, 100);
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                mobileMenuButtonClickedRef.current = true;
              }}
              className="p-2 text-gray-600 active:text-gray-900 transition-colors touch-manipulation"
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              type="button"
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* Mobile dropdown menu */}
            {isMenuOpen && (
              <div
                ref={mobileMenuDropdownRef}
                className="absolute right-0 top-full mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-[10000] overflow-hidden"
                style={{ willChange: "transform, opacity" }}
                onClick={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                  {/* Dropdown header */}
                  <div className="flex items-center p-4 border-b border-gray-200">
                    <h3 className="text-base font-semibold text-gray-900">Menu</h3>
                  </div>

                  {/* Navigation links */}
                  <div className="p-2 max-h-[calc(100vh-200px)] overflow-y-auto">
                    <Link
                      to="/products"
                      className="block px-3 py-2.5 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      All Products
                    </Link>
                    <Link
                      to="/categories"
                      className="block px-3 py-2.5 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Categories
                    </Link>
                    <Link
                      to="/stores"
                      className="block px-3 py-2.5 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Stores
                    </Link>

                    {!currentUser && (
                      <div className="pt-2 mt-2 border-t border-gray-200 space-y-2">
                        <Link
                          to="/login"
                          className="block w-full px-3 py-2 text-center text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          Sign In
                        </Link>
                        <Link
                          to="/register"
                          className="block w-full px-3 py-2 text-center text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          Sign Up
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    </header>
  );
};

// Premium Subcategory Column Component with caching
interface SubcategoryColumnProps {
  categoryId: string;
  categoryName: string;
  categoryImage?: string;
  cachedSubcategories?: Category[];
  onClose?: () => void;
  hoverTimeoutRef?: React.MutableRefObject<NodeJS.Timeout | null>;
  isHoveringMenuRef?: React.MutableRefObject<boolean>;
}

const SubcategoryColumn: React.FC<SubcategoryColumnProps> = React.memo(
  ({
    categoryId,
    categoryName,
    categoryImage,
    cachedSubcategories,
    onClose,
    hoverTimeoutRef,
    isHoveringMenuRef,
  }) => {
    // Only fetch if not cached - this prevents unnecessary API calls
    const shouldFetch =
      !cachedSubcategories || cachedSubcategories.length === 0;
    const { data: fetchedSubcategories = [], isLoading } = useSubcategories(
      shouldFetch ? categoryId : "" // Pass empty string to disable the query when cached
    );

    // Use cached data for instant display, fallback to fetched
    const subcategories =
      cachedSubcategories && cachedSubcategories.length > 0
        ? cachedSubcategories
        : fetchedSubcategories;
    const showLoading = isLoading && shouldFetch && subcategories.length === 0;

    return (
      <div
        className="h-full flex flex-col"
        onMouseEnter={(e) => {
          e.stopPropagation();
          if (isHoveringMenuRef) {
            isHoveringMenuRef.current = true;
          }
          if (hoverTimeoutRef?.current) {
            clearTimeout(hoverTimeoutRef.current);
          }
        }}
      >
        {/* Category Header */}
        <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-3">
            {categoryImage && (
              <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                <img
                  src={categoryImage}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 truncate">
                {categoryName}
              </h3>
              <p className="text-xs text-gray-600">
                {subcategories.length} subcategories
              </p>
            </div>
          </div>
        </div>

        {/* Subcategories List */}
        <div className="flex-1 overflow-y-auto py-2">
          {showLoading ? (
            <div className="px-4 space-y-2">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="h-10 bg-gray-50 rounded-lg animate-pulse"
                />
              ))}
            </div>
          ) : subcategories.length > 0 ? (
            <nav role="navigation" className="px-2">
              {subcategories.map((subcategory) => {
                const subImage =
                  subcategory.imageUrl ||
                  subcategory.thumbnailUrl ||
                  subcategory.image;
                return (
                  <Link
                    key={subcategory.id}
                    to={`/categories?category=${subcategory.id}`}
                    onClick={onClose}
                    className="group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-100"
                  >
                    {/* Subcategory thumbnail */}
                    <div className="w-8 h-8 rounded-md overflow-hidden bg-gray-100 flex-shrink-0 group-hover:ring-2 group-hover:ring-primary-200 transition-shadow duration-100">
                      {subImage ? (
                        <img
                          src={subImage}
                          alt=""
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Layers className="w-4 h-4 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <span className="flex-1 truncate font-medium">
                      {subcategory.name}
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-primary-500 transition-colors duration-100" />
                  </Link>
                );
              })}
            </nav>
          ) : (
            <div className="h-full flex items-center justify-center p-6">
              <div className="text-center">
                <Layers className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No subcategories yet</p>
              </div>
            </div>
          )}
        </div>

        {/* View All Footer */}
        {subcategories.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50">
            <Link
              to={`/categories?category=${categoryId}`}
              onClick={onClose}
              className="flex items-center justify-center gap-2 w-full py-2 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
            >
              View all in {categoryName}
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    );
  }
);

SubcategoryColumn.displayName = "SubcategoryColumn";

export default Header;
